
import { useState, useMemo } from 'react';
import { Service } from '@/components/services/types';
import { Product } from '@/components/services/products/types';

export const useExtendedServicesFilters = (
  services: Service[] = [], 
  products: Product[] = []
) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [sortBy, setSortBy] = useState('latest');
  const [priceRange, setPriceRange] = useState<[number, number]>([0, 1000]);
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);

  // Filtrer les services
  const filteredServices = useMemo(() => {
    if (!services || services.length === 0) return [];

    let filtered = [...services];

    // Filtrage par texte
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      filtered = filtered.filter(service => 
        service.service_name?.toLowerCase().includes(search) || 
        service.description?.toLowerCase().includes(search) ||
        service.category?.toLowerCase().includes(search)
      );
    }

    // Filtrage par catégorie
    if (selectedCategories.length > 0) {
      filtered = filtered.filter(service => 
        service.category && selectedCategories.includes(service.category)
      );
    }

    // Filtrage par prix
    filtered = filtered.filter(service => {
      const price = Number(service.price || 0);
      return price >= priceRange[0] && price <= priceRange[1];
    });

    // Tri des résultats
    if (sortBy === 'price-asc') {
      filtered.sort((a, b) => Number(a.price || 0) - Number(b.price || 0));
    } else if (sortBy === 'price-desc') {
      filtered.sort((a, b) => Number(b.price || 0) - Number(a.price || 0));
    } else if (sortBy === 'name') {
      filtered.sort((a, b) => a.service_name.localeCompare(b.service_name));
    } else if (sortBy === 'latest') {
      filtered.sort((a, b) => 
        new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      );
    }

    return filtered;
  }, [services, searchTerm, selectedCategories, priceRange, sortBy]);

  // Filtrer les produits
  const filteredProducts = useMemo(() => {
    if (!products || products.length === 0) return [];

    let filtered = [...products];

    // Filtrage par texte
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      filtered = filtered.filter(product => 
        product.title?.toLowerCase().includes(search) || 
        product.description?.toLowerCase().includes(search)
      );
    }

    // Filtrage par prix
    filtered = filtered.filter(product => {
      const price = Number(product.price || 0);
      return price >= priceRange[0] && price <= priceRange[1];
    });

    // Tri des résultats
    if (sortBy === 'price-asc') {
      filtered.sort((a, b) => Number(a.price || 0) - Number(b.price || 0));
    } else if (sortBy === 'price-desc') {
      filtered.sort((a, b) => Number(b.price || 0) - Number(a.price || 0));
    } else if (sortBy === 'name') {
      filtered.sort((a, b) => a.title.localeCompare(b.title));
    } else if (sortBy === 'latest') {
      filtered.sort((a, b) => 
        new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      );
    }

    return filtered;
  }, [products, searchTerm, priceRange, sortBy]);

  // Extraction des catégories uniques pour les filtres
  const availableCategories = useMemo(() => {
    const categories = services
      .map(service => service.category)
      .filter((category): category is string => !!category);
    
    return [...new Set(categories)].sort();
  }, [services]);

  return {
    searchTerm,
    setSearchTerm,
    viewMode,
    setViewMode,
    sortBy,
    setSortBy,
    priceRange,
    setPriceRange,
    selectedCategories,
    setSelectedCategories,
    availableCategories,
    filteredServices,
    filteredProducts
  };
};
